(() => {
  if (window.MistruneHelpR14) return;
  window.MistruneHelpR14 = true;
  const send = (event, detail = {}) => window.MistruneTelemetry?.track(event, detail);
  const contactTopics = new Set(['general', 'scent', 'product', 'order', 'return']);
  const externalOverlays = new Set();
  let surfaceCheckQueued = false;
  const inboxRoots = new WeakSet();
  const inboxActivator = (root, depth = 0) => {
    if (!root || depth > 5) return null;
    const button = root.querySelector('button[part~="activator"]');
    if (button) return button;
    for (const element of root.querySelectorAll('*')) {
      if (element.shadowRoot) {
        const found = inboxActivator(element.shadowRoot, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };
  const watchInboxRoot = (root, depth = 0) => {
    if (!root || depth > 5) return;
    if (!inboxRoots.has(root)) {
      inboxRoots.add(root);
      new MutationObserver(queueSurfaceCheck).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'aria-expanded'] });
    }
    root.querySelectorAll('*').forEach(element => {
      if (element.shadowRoot) watchInboxRoot(element.shadowRoot, depth + 1);
    });
  };
  const syncInbox = () => {
    const host = document.querySelector('shopify-chat');
    watchInboxRoot(host?.shadowRoot);
    const activator = inboxActivator(host?.shadowRoot);
    const open = Boolean(host?.hasAttribute('open'));
    const ready = Boolean(activator) || (open && host.hasAttribute('data-mr-help-integrated'));
    if (host && ready !== host.hasAttribute('data-mr-help-integrated')) host.toggleAttribute('data-mr-help-integrated', ready);
    document.querySelectorAll('[data-mr-inbox-open]').forEach(button => {
      if (button.hidden === ready) button.hidden = !ready;
    });
    document.querySelectorAll('[data-mr-floating-help]').forEach(panel => {
      if (panel.hasAttribute('data-inbox-ready') !== ready) panel.toggleAttribute('data-inbox-ready', ready);
    });
    // If Shopify changes its supported activator markup, keep its native entry visible.
    return { open, fallback: !ready && Boolean(host?.shadowRoot?.childElementCount) };
  };
  // The four current PDP sections have sticky galleries, not bottom action bars.
  // Only the verified builder actions are a storefront bottom-bar candidate.
  const bottomBarSelector = '.ma-builder__actions';
  // Shopify owns these optional hosts. Measure the outer box only; never access iframe contents.
  const previewBarSelector = '#PBarNextFrame, #preview-bar-iframe, #shopify-preview-bar-iframe, #shopify-preview-bar, #ShopifyPreviewBar, [data-shopify-preview-bar], iframe[id*="preview-bar"], iframe[id*="preview_bar"]';
  const surfaceVisible = node => !node.hidden && node.getAttribute('aria-hidden') !== 'true' && node.getClientRects().length > 0 && window.getComputedStyle(node).visibility !== 'hidden';
  const syncHelpPosition = () => {
    const panels = Array.from(document.querySelectorAll('[data-mr-floating-help]'));
    if (!panels.length) return;
    const layoutHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewport = window.visualViewport;
    const visibleTop = viewport?.offsetTop || 0;
    const visibleBottom = viewport ? Math.min(layoutHeight, visibleTop + viewport.height) : layoutHeight;
    const candidates = [];
    document.querySelectorAll(`${bottomBarSelector}, ${previewBarSelector}`).forEach(node => {
      if (!surfaceVisible(node)) return;
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.opacity === '0') return;
      const preview = node.matches(previewBarSelector);
      if (!preview && style.position !== 'fixed' && style.position !== 'sticky') return;
      const rect = node.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0 || rect.top >= visibleBottom || rect.bottom <= visibleTop || rect.right <= 0 || rect.left >= window.innerWidth) return;
      // Do not mistake a full-page preview iframe for a toolbar.
      if (preview && rect.height > (visibleBottom - visibleTop) * .4) return;
      candidates.push(rect);
    });
    // Bottommost first also handles an action bar placed immediately above a preview bar.
    candidates.sort((a, b) => b.bottom - a.bottom);
    let occupied = 0;
    for (const rect of candidates) {
      if (rect.bottom < visibleBottom - occupied - 32) continue;
      occupied = Math.max(occupied, visibleBottom - Math.max(visibleTop, rect.top));
    }
    const bottom = occupied > 0 ? `${Math.ceil(layoutHeight - visibleBottom + occupied + 12)}px` : '';
    panels.forEach(panel => {
      // Retain CSS's 20px desktop / 15px mobile default when no visible bar is docked.
      if (panel.style.getPropertyValue('--mr-help-bottom') === bottom) return;
      if (bottom) panel.style.setProperty('--mr-help-bottom', bottom);
      else panel.style.removeProperty('--mr-help-bottom');
    });
  };
  const syncHelpSurfaces = () => {
    const inbox = syncInbox();
    let blocked = externalOverlays.size > 0 || document.documentElement.dataset.mrHelpSuppressed === 'true' || document.documentElement.classList.contains('ma-menu-open');
    blocked ||= inbox.open || inbox.fallback;
    blocked ||= Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], [data-ma-menu][open], [data-ma-header].is-mega-open, #shopify-pc__banner, #shopify-pc__prefs, [data-mr-consent-open="true"]')).some(surfaceVisible);
    try { blocked ||= window.Shopify?.customerPrivacy?.shouldShowBanner() === true; } catch { blocked = true; }
    document.querySelectorAll('[data-mr-floating-help]').forEach(panel => {
      if (blocked && panel.open) panel.open = false;
      if (panel.hidden !== blocked) panel.hidden = blocked;
    });
  };
  const queueSurfaceCheck = () => {
    if (surfaceCheckQueued) return;
    surfaceCheckQueued = true;
    window.requestAnimationFrame(() => { surfaceCheckQueued = false; syncHelpSurfaces(); syncHelpPosition(); });
  };
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-mr-contact-note]').forEach(note => {
      if (note.dataset.mrReady) return;
      note.dataset.mrReady = 'true';
      const form = note.querySelector('form');
      const topic = note.querySelector('[data-mr-contact-topic]');
      const orderField = note.querySelector('[data-mr-contact-order]');
      if (!form || !topic || !orderField) return;
      const initialTopic = new URLSearchParams(window.location.search).get('topic');
      if (contactTopics.has(initialTopic)) topic.value = initialTopic;
      const updateOrderField = () => {
        const orderTopic = topic.value === 'order' || topic.value === 'return';
        orderField.hidden = !orderTopic;
        orderField.querySelector('input').disabled = !orderTopic;
      };
      updateOrderField();
      topic.addEventListener('change', updateOrderField);
      form.addEventListener('submit', () => {
        if (form.checkValidity()) send('contact_submit_attempt', { topic: topic.value });
      });
      const status = note.querySelector('[data-mr-contact-status]');
      if (status) {
        status.focus({ preventScroll: true });
        if (status.dataset.mrContactStatus === 'success') send('contact_form_success');
      }
    });
    scope.querySelectorAll('[data-mr-faq]').forEach(answer => {
      if (answer.dataset.mrReady) return;
      answer.dataset.mrReady = 'true';
      answer.addEventListener('toggle', () => {
        if (answer.open) send('faq_opened', { faq_id: answer.dataset.mrFaq, topic: answer.dataset.mrHelpTopic });
      });
    });
    scope.querySelectorAll('[data-mr-floating-help]').forEach(panel => {
      if (panel.dataset.mrReady) return;
      panel.dataset.mrReady = 'true';
      const close = panel.querySelector('[data-mr-help-close]');
      close.hidden = false;
      close.addEventListener('click', () => {
        panel.open = false;
        panel.querySelector('summary').focus();
      });
      panel.addEventListener('toggle', () => {
        if (panel.open) send('help_opened');
      });
    });
    syncHelpSurfaces();
    syncHelpPosition();
  };
  document.addEventListener('click', event => {
    const chat = event.target.closest('[data-mr-inbox-open]');
    if (chat) {
      const host = document.querySelector('shopify-chat');
      const activator = inboxActivator(host?.shadowRoot);
      if (activator && !host.hasAttribute('open')) {
        document.querySelectorAll('[data-mr-floating-help][open]').forEach(panel => { panel.open = false; });
        activator.click();
        send('help_chat_opened');
        queueSurfaceCheck();
      }
      return;
    }
    const link = event.target.closest('[data-mr-help-event]');
    if (!link) return;
    const detail = {};
    if (link.dataset.mrHelpTopic) detail.topic = link.dataset.mrHelpTopic;
    send(link.dataset.mrHelpEvent, detail);
  });
  document.addEventListener('pointerdown', event => {
    document.querySelectorAll('[data-mr-floating-help][open]').forEach(panel => {
      if (!panel.contains(event.target)) panel.open = false;
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-mr-floating-help][open]').forEach(panel => {
      const focusInside = panel.contains(document.activeElement);
      panel.open = false;
      if (focusInside) panel.querySelector('summary').focus();
    });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('visitorConsentCollected', queueSurfaceCheck);
  document.addEventListener('mistrune:overlay-change', event => {
    const source = typeof event.detail?.source === 'string' ? event.detail.source : 'external';
    if (event.detail?.open === true) externalOverlays.add(source);
    else if (event.detail?.open === false) externalOverlays.delete(source);
    queueSurfaceCheck();
  });
  document.addEventListener('mistrune:welcome-state', queueSurfaceCheck);
  document.addEventListener('mistrune:interaction', queueSurfaceCheck);
  window.addEventListener('scroll', queueSurfaceCheck, { passive: true });
  window.addEventListener('resize', queueSurfaceCheck, { passive: true });
  window.visualViewport?.addEventListener('resize', queueSurfaceCheck, { passive: true });
  window.visualViewport?.addEventListener('scroll', queueSurfaceCheck, { passive: true });
  customElements.whenDefined('shopify-chat').then(() => {
    queueSurfaceCheck();
    // The app creates its open shadow root asynchronously after custom-element registration.
    [250, 1000, 3000, 7000].forEach(delay => window.setTimeout(queueSurfaceCheck, delay));
  });
  const surfaceObserver = new MutationObserver(records => {
    // Our own offset write must not schedule a new frame through the style observer.
    if (records.some(record => !(record.type === 'attributes' && record.attributeName === 'style' && record.target.matches?.('[data-mr-floating-help]')))) queueSurfaceCheck();
  });
  surfaceObserver.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'class', 'hidden', 'aria-hidden', 'style', 'data-mr-help-suppressed', 'data-mr-consent-open'] });
})();
